import { useTranslation } from "react-i18next";
import {
  IconCheck,
  IconCornerDownLeft,
  IconLock,
  IconPlus,
  IconRefresh,
  IconSearch,
  IconSparkles,
} from "@tabler/icons-react";
import BookMarkFillIcon from "../../assets/icons/bookmark-fill.svg?react";
import type { Api } from "../../services/api";
import PlaylistThumbnail from "../cards/PlaylistThumbnail";
import { SuggestedNameInput } from "../ui/SuggestedNameInput";
import { cn } from "../../lib/utils";

export type Collection =
  Api["get"]["/webapp/private/playlists/me"]["output"]["body"]["docs"][number];

export function sameName(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

function Checkbox({ checked }: { checked: boolean }): React.JSX.Element {
  return (
    <span
      aria-hidden
      className={cn(
        "flex size-4.5 shrink-0 items-center justify-center rounded-md",
        checked
          ? "bg-ds-primary text-white"
          : "border-2 border-ds-slate-300 bg-white"
      )}
    >
      {checked ? <IconCheck className="size-3.5" stroke={2.5} /> : null}
    </span>
  );
}

function CollectionCover({
  collection,
}: {
  collection: Collection;
}): React.JSX.Element {
  const image = collection.images.at(0);
  return (
    <span className="size-8 shrink-0 overflow-hidden rounded-lg">
      {image === undefined ? (
        <PlaylistThumbnail backgroundColor={null} />
      ) : (
        <img
          src={image.sizes === null ? image.url : image.sizes.thumbnail.file}
          alt=""
          className="size-full object-cover"
        />
      )}
    </span>
  );
}

function Row({
  checked,
  cover,
  title,
  meta,
  highlighted,
  onClick,
}: {
  checked: boolean;
  cover: React.ReactNode;
  title: string;
  meta: React.ReactNode;
  highlighted: boolean;
  onClick: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      onClick={onClick}
      className={cn(
        "flex w-full cursor-pointer items-center gap-2.5 rounded-lg border-none px-3 py-1.5 text-left hover:bg-ds-slate-100",
        highlighted ? "bg-ds-slate-100" : "bg-transparent"
      )}
    >
      <Checkbox checked={checked} />
      {cover}
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-ui-note font-semibold text-ds-black">
          {title}
        </span>
        <span className="flex items-center gap-1 text-ui-caption text-ds-slate-600">
          {meta}
        </span>
      </span>
    </button>
  );
}

function CollectionRow({
  collection,
  articleId,
  highlighted,
  onToggle,
}: {
  collection: Collection;
  articleId: string;
  highlighted: boolean;
  onToggle: (collection: Collection) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const count = t("{{count}} articles", {
    count: collection.metrics.articles,
  });
  return (
    <Row
      checked={collection.playlist.includes(articleId)}
      cover={<CollectionCover collection={collection} />}
      title={collection.title}
      meta={
        collection.isPublic ? (
          t("{{count}} articles · shared by link", {
            count: collection.metrics.articles,
          })
        ) : (
          <>
            {count}
            <IconLock
              className="size-4"
              stroke={1.25}
              aria-label={t("Private")}
            />
          </>
        )
      }
      highlighted={highlighted}
      onClick={() => {
        onToggle(collection);
      }}
    />
  );
}

function PrivateNote(): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <span className="flex min-w-0 flex-1 items-center gap-1.5 text-ui-caption text-ds-slate-600">
      <IconLock className="size-4 shrink-0" stroke={1.25} />
      {t("Private. Only you can see it.")}
    </span>
  );
}

function NewCollectionField({
  typed,
  suggestions,
  canCreate,
  onNameChange,
  onMoreSuggestions,
  onCreate,
}: {
  typed: string | null;
  suggestions: readonly string[];
  /** False until the collections have loaded, which the duplicate check needs. */
  canCreate: boolean;
  onNameChange: (name: string) => void;
  onMoreSuggestions: () => void;
  onCreate: (name: string) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const suggested = suggestions.at(0);
  // Until the reader types, the field holds JuisciGPT's first idea.
  const value =
    typed !== null ? typed : suggested === undefined ? "" : suggested;
  const otherIdeas = suggestions.filter((idea) => idea !== value).slice(0, 2);

  return (
    <div className="flex flex-col gap-2 p-1">
      {suggestions.length === 0 ? null : (
        <span className="flex items-center gap-1 pl-1 text-ui-caption font-medium text-ds-primary">
          <IconSparkles className="size-4" stroke={1.25} />
          {t("Suggested by JuisciGPT from this article")}
        </span>
      )}
      <SuggestedNameInput
        value={value}
        isSuggested={typed === null && value !== ""}
        placeholder={t("Name your collection")}
        onChange={onNameChange}
        onSubmit={() => {
          onCreate(value);
        }}
      />
      {suggestions.length === 0 ? null : (
        <div className="flex flex-wrap items-center gap-1.5 pl-0.5">
          {otherIdeas.map((idea) => (
            <button
              key={idea}
              type="button"
              onClick={() => {
                onNameChange(idea);
              }}
              className="cursor-pointer rounded-full border border-ds-black/10 bg-white px-2.5 py-1 text-xs font-medium text-ds-slate-700 hover:bg-ds-slate-100"
            >
              {idea}
            </button>
          ))}
          <button
            type="button"
            aria-label={t("Suggest other names")}
            title={t("Suggest other names")}
            onClick={onMoreSuggestions}
            className="flex size-6.5 cursor-pointer items-center justify-center rounded-full border-none bg-transparent text-ds-slate-600 hover:bg-ds-slate-100"
          >
            <IconRefresh className="size-4" stroke={1.25} />
          </button>
        </div>
      )}
      <div className="flex items-center gap-1.5 pl-1">
        <PrivateNote />
        <button
          type="button"
          disabled={!canCreate || value.trim() === ""}
          onClick={() => {
            onCreate(value);
          }}
          className="shrink-0 cursor-pointer rounded-lg border-none bg-ds-moss-900 px-3 py-1 text-ui-note font-semibold text-white disabled:cursor-default disabled:bg-ds-slate-300"
        >
          {t("Create")}
        </button>
      </div>
    </div>
  );
}

/**
 * The body of the save popover (Figma "D3.save"): a field that finds or
 * creates a collection, the Saved list and the reader's collections as
 * checkboxes, and the "+ New collection" field. Every change applies at once;
 * there is no Save button.
 */
export function SaveToCollectionPanel({
  query,
  savedCount,
  inSavedList,
  collections,
  articleId,
  naming,
  onQueryChange,
  onQuerySubmit,
  onToggleSavedList,
  onToggle,
  onStartNaming,
  onRetryCollections,
  onNameChange,
  onMoreSuggestions,
  onCreate,
}: {
  query: string;
  savedCount: number | null;
  /** The Saved list alone holds the article: its collections are apart. */
  inSavedList: boolean;
  collections:
    | { state: "loading" }
    | { state: "error" }
    | { state: "ready"; docs: readonly Collection[] };
  articleId: string;
  naming: { typed: string | null; suggestions: readonly string[] } | null;
  onQueryChange: (query: string) => void;
  onQuerySubmit: () => void;
  onToggleSavedList: (isIn: boolean) => void;
  onToggle: (collection: Collection) => void;
  onStartNaming: () => void;
  onRetryCollections: () => void;
  onNameChange: (name: string) => void;
  onMoreSuggestions: () => void;
  onCreate: (name: string) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const docs = collections.state === "ready" ? collections.docs : [];
  const searching = query.trim() !== "";
  const matches = docs.filter((collection) =>
    collection.title.toLowerCase().includes(query.trim().toLowerCase())
  );
  const exact = matches.find((collection) => sameName(collection.title, query));

  return (
    <div className="flex flex-col gap-1">
      <label
        className={cn(
          "flex items-center gap-2 rounded-lg border bg-white px-2.5 py-2",
          searching ? "border-ds-black" : "border-ds-slate-100"
        )}
      >
        <IconSearch
          className="size-4 shrink-0 text-ds-slate-600"
          stroke={1.25}
        />
        <input
          // The popover opens for this field: the reader can type a
          // collection's name straight away.
          autoFocus={naming === null}
          value={query}
          placeholder={t("Find or create a collection")}
          maxLength={80}
          onChange={(event) => {
            onQueryChange(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" && searching) onQuerySubmit();
          }}
          className="min-w-0 flex-1 border-none bg-transparent p-0 text-ui-note text-ds-black outline-none placeholder:text-ds-slate-600"
        />
      </label>

      <div className="flex max-h-64 flex-col overflow-y-auto pt-1">
        {collections.state === "error" ? (
          <p className="m-0 flex items-center justify-between gap-2 px-3 py-2 text-ui-caption text-ds-clementine-600">
            {t("Your collections could not be loaded.")}
            <button
              type="button"
              onClick={onRetryCollections}
              className="shrink-0 cursor-pointer border-none bg-transparent p-0 font-semibold text-ds-primary hover:underline"
            >
              {t("Try again")}
            </button>
          </p>
        ) : null}
        {searching ? null : (
          <Row
            checked={inSavedList}
            cover={
              <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-ds-moss-100 text-ds-moss-900 [&>svg]:size-4">
                <BookMarkFillIcon />
              </span>
            }
            title={t("Saved")}
            meta={
              savedCount === null
                ? null
                : t("{{count}} articles · all your saves", {
                    count: savedCount,
                  })
            }
            highlighted={false}
            onClick={() => {
              onToggleSavedList(!inSavedList);
            }}
          />
        )}
        {searching && exact === undefined ? (
          <button
            type="button"
            disabled={collections.state !== "ready"}
            onClick={onQuerySubmit}
            className="flex w-full cursor-pointer items-center gap-2.5 rounded-lg border-none bg-ds-mint-50 px-3 py-2 text-left disabled:cursor-default disabled:opacity-50"
          >
            <IconPlus
              className="size-4 shrink-0 text-ds-primary"
              stroke={1.5}
            />
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-ui-note font-semibold text-ds-primary">
                {t("Create “{{name}}”", { name: query.trim() })}
              </span>
              <span className="text-ui-caption text-ds-slate-600">
                {t("Private. Only you can see it.")}
              </span>
            </span>
            <IconCornerDownLeft
              className="size-3.5 shrink-0 text-ds-slate-600"
              stroke={1.25}
            />
          </button>
        ) : null}
        {(searching ? matches : docs).map((collection) => (
          <CollectionRow
            key={collection.id}
            collection={collection}
            articleId={articleId}
            highlighted={collection === exact}
            onToggle={onToggle}
          />
        ))}
      </div>

      {searching ? (
        <p className="m-0 border-b border-ds-slate-100 px-3 py-2 text-ui-caption text-ds-slate-600">
          {exact !== undefined
            ? t(
                "You already have this collection. Press ↵ to save the article to it."
              )
            : matches.length === 0
              ? t("No collection matches “{{name}}”.", { name: query.trim() })
              : null}
        </p>
      ) : (
        <>
          <div className="h-px w-full bg-ds-slate-100" />
          {naming === null ? (
            <button
              type="button"
              onClick={onStartNaming}
              className="flex w-full cursor-pointer items-center gap-2.5 rounded-lg border-none bg-transparent px-3 py-2 text-left text-ui-note font-semibold text-ds-black hover:bg-ds-slate-100"
            >
              <IconPlus className="size-4" stroke={1.5} />
              {t("New collection")}
            </button>
          ) : (
            <NewCollectionField
              typed={naming.typed}
              suggestions={naming.suggestions}
              canCreate={collections.state === "ready"}
              onNameChange={onNameChange}
              onMoreSuggestions={onMoreSuggestions}
              onCreate={onCreate}
            />
          )}
        </>
      )}
    </div>
  );
}
